import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartType } from '../../../models/widget-chart';

@Component({
  standalone: true,
  selector: 'via-basic-chart',
  templateUrl: './basic-chart.component.html',
  styleUrl: './basic-chart.component.scss'
})
export class BasicChartComponent implements OnChanges {
  @Input() type: ChartType = ChartType.Bar;
  @Input() data: { labels: string[]; values: number[] } = { labels: [], values: [] };

  // computed
  maxValue = 1;
  normalized: number[] = [];
  total = 1;
  pieSlices: { start: number; end: number; path: string }[] = [];

  // SVG polyline points for line chart
  linePoints = '';

  // pie label positions and text
  pieLabels: { x: number; y: number; text: string; angle: number }[] = [];

  ngOnChanges(_changes: SimpleChanges) {
    const vals = (this.data && this.data.values) || [];
    this.maxValue = Math.max(1, ...vals);
    this.total = vals.reduce((s, v) => s + (v || 0), 0) || 1;
    this.normalized = vals.map(v => (v || 0) / this.maxValue);
    // compute polyline points string for line chart
    if (this.normalized.length > 0) {
      const denom = (this.normalized.length - 1) || 1;
      this.linePoints = this.normalized
        .map((v, i) => `${(i / denom) * 100},${60 - (v * 48) - 8}`)
        .join(' ');
    } else {
      this.linePoints = '';
    }
    if (this.type === ChartType.Pie) {
      this.computePie();
    }
  }

  // helpers for pie generation
  private polarToCartesian(cx: number, cy: number, r: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: cx + (r * Math.cos(angleInRadians)),
      y: cy + (r * Math.sin(angleInRadians))
    };
  }

  private describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
    const start = this.polarToCartesian(cx, cy, r, endAngle);
    const end = this.polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
  }

  private computePie() {
    const vals = this.data.values || [];
    const sum = vals.reduce((s, v) => s + (v || 0), 0) || 1;
    let acc = 0;
    this.pieSlices = [];
    this.pieLabels = [];

    vals.forEach((v, idx) => {
      const start = (acc / sum) * 360;
      acc += (v || 0);
      const end = (acc / sum) * 360;
      this.pieSlices.push({ start, end, path: this.describeArc(50, 50, 48, start, end) });

      // compute centroid angle for label placement (mid-angle)
      const mid = (start + end) / 2;
      // place label a bit inside the slice (radius 32)
      const labelR = 34;
      const coord = this.polarToCartesian(50, 50, labelR, mid);
      // percentage text
      const pct = Math.round(((v || 0) / sum) * 100);
      const labelText = (this.data.labels && this.data.labels[idx]) ? `${this.data.labels[idx]} (${pct}%)` : `${pct}%`;
      this.pieLabels.push({ x: coord.x, y: coord.y, text: labelText, angle: mid });
    });
  }
}
